import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import MainLayout, { BreadcrumbItem } from '../../components/MainLayout';
import MisRecursosPage from './MisRecursosPage';
import MateriaDetailPage from './MateriaDetailPage';
import AgregarMateriaDialog from './AgregarMateriaDialog';
import CodigoMateriaDialog from './CodigoMateriaDialog';
import { IMiRecurso, IDocenteMateria, IAlumnoEnrollment } from '../../types/miRecurso';
import { ISubject } from '../../types/departamento';
import { RecursoInput } from './SubirRecursoModal';
import { RootState } from '../../state-management/store';
import { apiErrorMessage, listCatalogSubjects } from '../../api/catalog';
import { claimCourse, listTeacherCourses, regenerateCourseCode, releaseCourse, CourseResponse } from '../../api/courses';
import { listTeacherEnrollments, removeStudent, EnrollmentResponse } from '../../api/enrollments';
import {
	deleteResource,
	listTeacherResources,
	replaceResource,
	uploadResource,
} from '../../api/resources';
import {
	setRecursos,
	setMaterias,
	setAlumnos,
	setSelectedMateriaId,
	setSelectedStatus,
	setSelectedDetailTab,
	setSearchQuery,
	setPendingRemoveAlumno,
	updateMateriaCodigo,
	setUploadDialogOpen,
	setAddMateriaDialogOpen,
	setCodigoDialogMateriaId,
	resetMisRecursosPage,
} from '../../state-management/slices/MisRecursosPage';

const toMateria = (course: CourseResponse): IDocenteMateria => ({
	id: course.id,
	subjectId: course.subjectId,
	nombre: course.subjectName,
	codigo: course.code,
	resourceCount: course.resourceCount,
	alumnoCount: course.studentCount,
	hasChangesRequested: course.hasChangesRequested,
});

const toEnrollment = (row: EnrollmentResponse): IAlumnoEnrollment => ({
	id: row.id,
	materiaId: row.courseId,
	alumnoId: row.studentId,
	alumnoNombre: row.studentName,
	alumnoAvatarUrl: row.studentAvatarUrl,
	alumnoEmail: row.studentEmail,
	enrolledAt: new Date(row.enrolledAt),
});

const MisRecursosContainer: React.FC = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const [pageError, setPageError] = useState<string | null>(null);
	const [catalog, setCatalog] = useState<ISubject[]>([]);
	const [replacing, setReplacing] = useState<IMiRecurso | null>(null);
	const [releaseOpen, setReleaseOpen] = useState(false);

	const {
		recursos,
		materias,
		alumnos,
		selectedMateriaId,
		selectedStatus,
		selectedDetailTab,
		searchQuery,
		pendingRemoveAlumno,
		uploadDialogOpen,
		addMateriaDialogOpen,
		codigoDialogMateriaId,
	} = useSelector((state: RootState) => state.misRecursosPage);

	const reloadCourses = useCallback(async () => {
		const courses = await listTeacherCourses();
		dispatch(setMaterias(courses.map(toMateria)));
	}, [dispatch]);

	const reloadEnrollments = useCallback(async () => {
		const rows = await listTeacherEnrollments();
		dispatch(setAlumnos(rows.map(toEnrollment)));
	}, [dispatch]);

	const reloadResources = useCallback(
		async (courseId: number) => {
			const rows = await listTeacherResources({ courseId });
			dispatch(setRecursos(rows));
		},
		[dispatch]
	);

	useEffect(() => {
		let cancelled = false;
		dispatch(resetMisRecursosPage());
		setIsLoading(true);
		setPageError(null);
		Promise.all([listTeacherCourses(), listTeacherEnrollments()])
			.then(([courses, rows]) => {
				if (cancelled) return;
				dispatch(setMaterias(courses.map(toMateria)));
				dispatch(setAlumnos(rows.map(toEnrollment)));
			})
			.catch((error: unknown) => {
				if (!cancelled) setPageError(apiErrorMessage(error));
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [location.pathname, dispatch]);

	useEffect(() => {
		if (selectedMateriaId === null) {
			dispatch(setRecursos([]));
			return;
		}
		let cancelled = false;
		listTeacherResources({ courseId: selectedMateriaId })
			.then((rows) => {
				if (!cancelled) dispatch(setRecursos(rows));
			})
			.catch((error: unknown) => {
				if (!cancelled) setPageError(apiErrorMessage(error));
			});
		return () => {
			cancelled = true;
		};
	}, [selectedMateriaId, dispatch]);

	useEffect(() => {
		if (!addMateriaDialogOpen) return;
		let cancelled = false;
		listCatalogSubjects()
			.then((rows) => {
				if (!cancelled) setCatalog(rows);
			})
			.catch((error: unknown) => {
				if (!cancelled) setPageError(apiErrorMessage(error));
			});
		return () => {
			cancelled = true;
		};
	}, [addMateriaDialogOpen]);

	const filteredRecursos = useMemo(() => {
		let filtered = recursos.filter((recurso) => recurso.materiaId === selectedMateriaId);
		if (selectedStatus !== 'all') filtered = filtered.filter((recurso) => recurso.status === selectedStatus);
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter((recurso) => recurso.name.toLowerCase().includes(query));
		}
		return filtered;
	}, [recursos, selectedMateriaId, selectedStatus, searchQuery]);

	const filteredAlumnos = useMemo(() => {
		let filtered = alumnos.filter((alumno) => alumno.materiaId === selectedMateriaId);
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter(
				(alumno) =>
					alumno.alumnoNombre.toLowerCase().includes(query) ||
					alumno.alumnoEmail.toLowerCase().includes(query)
			);
		}
		return filtered;
	}, [alumnos, selectedMateriaId, searchQuery]);

	const selectedMateria = useMemo(
		() => materias.find((materia) => materia.id === selectedMateriaId) ?? null,
		[materias, selectedMateriaId]
	);

	const claimedSubjectIds = useMemo(() => materias.map((materia) => materia.subjectId), [materias]);

	const handleSelectMateria = (materia: IDocenteMateria) => {
		dispatch(setSelectedMateriaId(materia.id));
	};

	const handleBackToHub = () => {
		dispatch(setSelectedMateriaId(null));
	};

	const handleConfirmarAgregarMateria = async (subjectId: number) => {
		const course = await claimCourse(subjectId);
		await reloadCourses();
		dispatch(setAddMateriaDialogOpen(false));
		dispatch(setCodigoDialogMateriaId(course.id));
	};

	const handleConfirmarSubida = async (input: RecursoInput) => {
		if (selectedMateriaId === null) return;
		if (replacing) {
			await replaceResource(replacing.id, input);
		} else {
			await uploadResource(selectedMateriaId, input);
		}
		setReplacing(null);
		dispatch(setUploadDialogOpen(false));
		await reloadResources(selectedMateriaId);
		await reloadCourses();
	};

	const handleSubirNuevaVersion = (recurso: IMiRecurso) => {
		setReplacing(recurso);
		dispatch(setUploadDialogOpen(true));
	};

	const handleBorrar = async (recurso: IMiRecurso) => {
		try {
			await deleteResource(recurso.id);
			if (selectedMateriaId !== null) await reloadResources(selectedMateriaId);
			await reloadCourses();
		} catch (error) {
			setPageError(apiErrorMessage(error));
		}
	};

	const handleConfirmRemoveAlumno = async () => {
		if (!pendingRemoveAlumno) return;
		await removeStudent(pendingRemoveAlumno.materiaId, pendingRemoveAlumno.alumnoId);
		dispatch(setPendingRemoveAlumno(null));
		await reloadEnrollments();
		await reloadCourses();
	};

	const handleRegenerarCodigo = async (courseId: number) => {
		const course = await regenerateCourseCode(courseId);
		dispatch(updateMateriaCodigo({ materiaId: courseId, newCodigo: course.code }));
	};

	const handleConfirmLiberarMateria = async () => {
		if (selectedMateriaId === null) return;
		await releaseCourse(selectedMateriaId);
		setReleaseOpen(false);
		dispatch(setSelectedMateriaId(null));
		await reloadCourses();
	};

	const breadcrumbs: BreadcrumbItem[] = (() => {
		const crumbs: BreadcrumbItem[] = [{ label: 'Mis recursos', onClick: handleBackToHub }];
		if (selectedMateria) crumbs.push({ label: selectedMateria.nombre });
		return crumbs;
	})();

	const { pageTitle, pageSubtitle } = selectedMateria
		? {
				pageTitle: selectedMateria.nombre,
				pageSubtitle: 'Administra los recursos y alumnos de esta materia',
			}
		: {
				pageTitle: 'Mis recursos',
				pageSubtitle: 'Administra tus materias y los archivos que has subido',
			};

	const codigoDialogMateria = useMemo(
		() => materias.find((materia) => materia.id === codigoDialogMateriaId) ?? null,
		[materias, codigoDialogMateriaId]
	);

	return (
		<MainLayout pageTitle={pageTitle} pageSubtitle={pageSubtitle} breadcrumbs={breadcrumbs}>
			{pageError && (
				<Alert severity='error' onClose={() => setPageError(null)}>
					{pageError}
				</Alert>
			)}
			{selectedMateria ? (
				<MateriaDetailPage
					materia={selectedMateria}
					recursos={filteredRecursos}
					alumnos={filteredAlumnos}
					selectedStatus={selectedStatus}
					selectedDetailTab={selectedDetailTab}
					searchQuery={searchQuery}
					pendingRemoveAlumno={pendingRemoveAlumno}
					onSearchChange={(query) => dispatch(setSearchQuery(query))}
					onStatusChange={(status) => dispatch(setSelectedStatus(status))}
					onResetFilters={() => dispatch(setSelectedStatus('all'))}
					onTabChange={(tab) => dispatch(setSelectedDetailTab(tab))}
					onSubirNuevoRecurso={() => {
						setReplacing(null);
						dispatch(setUploadDialogOpen(true));
					}}
					onSubirNuevaVersion={handleSubirNuevaVersion}
					onBorrar={(recurso) => void handleBorrar(recurso)}
					onVerCodigo={() => dispatch(setCodigoDialogMateriaId(selectedMateriaId))}
					onRemoveAlumno={(enrollment) => dispatch(setPendingRemoveAlumno(enrollment))}
					onConfirmRemoveAlumno={handleConfirmRemoveAlumno}
					onCancelRemoveAlumno={() => dispatch(setPendingRemoveAlumno(null))}
					releaseOpen={releaseOpen}
					onEliminarMateria={() => setReleaseOpen(true)}
					onConfirmLiberarMateria={handleConfirmLiberarMateria}
					onCancelLiberarMateria={() => setReleaseOpen(false)}
					isSubirRecursoModalOpen={uploadDialogOpen}
					uploadTitle={replacing ? 'Nueva versión' : 'Subir recurso'}
					onCloseModal={() => {
						setReplacing(null);
						dispatch(setUploadDialogOpen(false));
					}}
					onConfirmarSubida={handleConfirmarSubida}
				/>
			) : (
				<MisRecursosPage
					materias={materias}
					isLoading={isLoading}
					onSelectMateria={handleSelectMateria}
					onAgregarMateria={() => dispatch(setAddMateriaDialogOpen(true))}
				/>
			)}

			<AgregarMateriaDialog
				open={addMateriaDialogOpen}
				options={catalog.map((subject) => ({ id: subject.id, nombre: subject.name }))}
				claimedSubjectIds={claimedSubjectIds}
				onClose={() => dispatch(setAddMateriaDialogOpen(false))}
				onConfirmar={handleConfirmarAgregarMateria}
			/>

			<CodigoMateriaDialog
				open={codigoDialogMateriaId !== null}
				materia={codigoDialogMateria}
				onClose={() => dispatch(setCodigoDialogMateriaId(null))}
				onRegenerarCodigo={handleRegenerarCodigo}
			/>
		</MainLayout>
	);
};

export default MisRecursosContainer;
